import * as Common from '@/utils/Common';

/**
 * Keeps the label's default font size until the text exceeds the printable
 * length, then scales it down in proportion to the weighted text length.
 * Common.getTextLength counts Korean characters as two positions.
 */
export function getAdaptiveFontSize(value, defaultFontSize, printableLength) {
    const text = value === undefined || value === null ? '' : String(value);
    const textLength = Common.getTextLength(text);

    if (textLength <= printableLength) {
        return `${defaultFontSize}pt`;
    }

    const fittedFontSize = Math.floor((defaultFontSize * printableLength / textLength) * 10) / 10;
    return `${fittedFontSize}pt`;
}

const DEFAULT_SUB_PRINT_ARGB = 'FF000000';

export const SUB_PRINT_POSITION_VALUES = Object.freeze({
    FRT: 'FF0100FFFRT',
    RR: 'FFFF0000RR',
    NONE: '',
});

export function getSubPrintRawValue(data) {
    if (!data || typeof data !== 'object') return '';

    return data.TMP41 ?? data.tmp41 ?? data.WRCPSUB ?? data.wrcpsub ?? '';
}

/**
 * WRCPSUB format: first 8 characters are an ARGB hex color, and the
 * remaining characters are the optional sub-print text. Its visibility is
 * independent of the LH/RH marker size; when a large marker is present, the
 * text is positioned immediately to its left.
 * All stored/transferred color values remain ARGB. The display color is
 * exposed separately as rgba() for compatibility with browser print engines.
 * Example: FF0100FFFRT -> ARGB #FF0100FF, CSS rgba(1, 0, 255, 1), text FRT.
 */
export function parseSubPrintValue(value) {
    const raw = value === undefined || value === null ? '' : String(value).trim();
    const rawColorCode = raw.slice(0, 8).toUpperCase();
    const colorCode = /^[0-9A-F]{8}$/.test(rawColorCode) ? rawColorCode : DEFAULT_SUB_PRINT_ARGB;
    const alpha = Math.round((parseInt(colorCode.slice(0, 2), 16) / 255) * 1000) / 1000;
    const red = parseInt(colorCode.slice(2, 4), 16);
    const green = parseInt(colorCode.slice(4, 6), 16);
    const blue = parseInt(colorCode.slice(6, 8), 16);
    const text = raw.slice(8).trim();
    const normalizedText = text.toUpperCase();
    const position = normalizedText === 'FRT' || normalizedText === 'RR' ? normalizedText : '';

    return {
        colorCode,
        argbColor: `#${colorCode}`,
        color: `#${colorCode}`,
        cssColor: `rgba(${red}, ${green}, ${blue}, ${alpha})`,
        text,
        position,
    };
}

export const SUB_PRINT_FONT_SIZE = '50pt';
